/**
 * EmailDashboard – Página principal de E-mail Marketing (estilo RD Station)
 * Dashboard com abas Enviados / Rascunhos / Agendados / Modelos
 */

import { useState, useMemo } from "react";
import { useLocation } from "wouter";
import { trpc } from "@/lib/trpc";
import { useContextoDeLista } from "@/features/assistente/contexto-de-tela";
import DashboardLayout from "@/components/DashboardLayout";
import { PermissionGuard } from "@/components/PermissionGuard";
import { usePermissions } from "@/hooks/usePermissions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import {
  Empty,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  EmptyDescription,
} from "@/components/ui/empty";
import {
  Mail,
  Plus,
  Search,
  Settings,
  HeartPulse,
  MoreHorizontal,
  Send,
  FileEdit,
  Clock,
  LayoutTemplate,
  Download,
  Trash2,
  Copy,
  BarChart2,
  Eye,
  XCircle,
  BookmarkPlus,
  FolderOpen,
  Inbox,
  Pencil,
} from "lucide-react";
import { toast } from "sonner";
import { toUserFriendlyError } from "@/lib/error-messages";
import { downloadCsv } from "@/lib/download";

// ─── Status labels ──────────────────────────────────────────────────────────

const EMAIL_STATUS_CONFIG: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
  pending:   { label: "Pendente",   variant: "secondary" },
  sent:      { label: "Enviado",    variant: "default" },
  delivered: { label: "Entregue",   variant: "default" },
  opened:    { label: "Aberto",     variant: "default" },
  clicked:   { label: "Clicado",    variant: "default" },
  bounced:   { label: "Rejeitado",  variant: "destructive" },
  failed:    { label: "Falhou",     variant: "destructive" },
};

const SCHEDULE_STATUS_CONFIG: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
  scheduled: { label: "Agendado",  variant: "secondary" },
  sending:   { label: "Enviando",  variant: "default" },
  sent:      { label: "Enviado",   variant: "default" },
  error:     { label: "Falhou",    variant: "destructive" },
  draft:     { label: "Cancelado", variant: "outline" },
};

// ─── Period filter helper ───────────────────────────────────────────────────

function getDateFromPeriod(period: string): Date | null {
  const now = new Date();
  switch (period) {
    case "today":
      return new Date(now.getFullYear(), now.getMonth(), now.getDate());
    case "7d":
      return new Date(now.getTime() - 7 * 86400000);
    case "14d":
      return new Date(now.getTime() - 14 * 86400000);
    case "30d":
      return new Date(now.getTime() - 30 * 86400000);
    case "6m":
      return new Date(now.getTime() - 180 * 86400000);
    default:
      return null;
  }
}

// ─── Stats cards ────────────────────────────────────────────────────────────

function StatsBar({ stats }: {
  stats: { total: number; sent: number; delivered: number; opened: number; clicked: number; bounced: number; failed: number };
}) {
  // Taxa de abertura/clique pode aparecer > 100% quando eventos do SendGrid
  // chegam fora de ordem (openedAt populado sem deliveredAt). Clampamos em
  // 100% defensivamente para evitar exibir "2220%" enquanto o pipeline de
  // webhooks não for endurecido. Preferimos sent como denominador estável.
  const openDenom = stats.delivered > 0 ? stats.delivered : stats.sent;
  const openRate = openDenom > 0 ? Math.min(100, Math.round((stats.opened / openDenom) * 100)) : 0;
  const clickDenom = stats.opened > 0 ? stats.opened : openDenom;
  const clickRate = clickDenom > 0 ? Math.min(100, Math.round((stats.clicked / clickDenom) * 100)) : 0;

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
      {[
        { label: "Enviados", value: stats.sent, color: "text-blue-600" },
        { label: "Entregues", value: stats.delivered, color: "text-green-600" },
        { label: "Abertos", value: stats.opened, color: "text-purple-600" },
        { label: "Clicados", value: stats.clicked, color: "text-indigo-600" },
        { label: "Taxa abertura", value: `${openRate}%`, color: "text-purple-600" },
        { label: "Taxa clique", value: `${clickRate}%`, color: "text-indigo-600" },
      ].map(({ label, value, color }) => (
        <Card key={label} className="px-4 py-3 text-center">
          <div className={`text-xl font-bold ${color}`}>{value}</div>
          <div className="text-xs text-muted-foreground mt-0.5">{label}</div>
        </Card>
      ))}
    </div>
  );
}

// ─── Empty state ────────────────────────────────────────────────────────────

function TabEmptyState({
  icon: Icon,
  title,
  description,
  actionLabel,
  onAction,
}: {
  icon: React.ElementType;
  title: string;
  description: string;
  actionLabel?: string;
  onAction?: () => void;
}) {
  return (
    <Empty className="py-16">
      <EmptyHeader>
        <EmptyMedia variant="icon">
          <Icon className="h-5 w-5" />
        </EmptyMedia>
        <EmptyTitle>{title}</EmptyTitle>
        <EmptyDescription>{description}</EmptyDescription>
      </EmptyHeader>
      {actionLabel && onAction && (
        <Button onClick={onAction} className="gap-2">
          <Plus className="h-4 w-4" />
          {actionLabel}
        </Button>
      )}
    </Empty>
  );
}

// ─── Export modal ───────────────────────────────────────────────────────────

/**
 * Export da lista de campanhas do período.
 *
 * Nasceu como um formulário com formato XLSX e seletor de campos que o botão
 * ignorava — clicar respondia "será implementada em breve". Ficou só o que o
 * servidor entrega de fato: o período, em CSV. Um controle que não faz nada é
 * pior do que controle nenhum, porque o usuário planeja o trabalho em cima da
 * promessa.
 *
 * O log por destinatário fica na tela da campanha, não aqui.
 */

const EXPORT_PERIODS = [
  { value: "7d", label: "Últimos 7 dias", days: 7 },
  { value: "14d", label: "Últimos 14 dias", days: 14 },
  { value: "30d", label: "Últimos 30 dias", days: 30 },
  { value: "6m", label: "Últimos 6 meses", days: 182 },
  { value: "all", label: "Todo o período", days: null },
] as const;

function ExportModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [exportPeriod, setExportPeriod] = useState<string>("30d");

  const exportMut = trpc.emailCampaigns.exportCampaignsList.useMutation({
    onSuccess: (res) => {
      downloadCsv(res.csv, res.fileName);
      toast.success(`${res.total} campanha(s) exportada(s).`);
      onClose();
    },
    onError: (e) => toast.error(toUserFriendlyError(e, "Erro ao exportar relatório")),
  });

  const handleExport = () => {
    const period = EXPORT_PERIODS.find(p => p.value === exportPeriod);
    const periodStart = period?.days
      ? new Date(Date.now() - period.days * 24 * 60 * 60 * 1000).toISOString()
      : undefined;
    exportMut.mutate({ periodStart });
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="w-[95vw] max-w-md mx-2 sm:mx-0">
        <DialogHeader>
          <DialogTitle>Exportar Relatório</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label className="text-sm">Período</Label>
            <Select value={exportPeriod} onValueChange={setExportPeriod}>
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {EXPORT_PERIODS.map(p => (
                  <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <p className="text-sm text-muted-foreground">
            Gera um CSV com uma linha por campanha: destinatários, entregas,
            aberturas, cliques, bounces e as taxas.
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={handleExport} disabled={exportMut.isPending} className="gap-2">
            <Download className="h-4 w-4" />
            {exportMut.isPending ? "Gerando..." : "Exportar relatório"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Main page ──────────────────────────────────────────────────────────────

export default function EmailDashboard() {
  const [, navigate] = useLocation();
  // exportCampaignsList exige email:export_reports — sem o gate, papel custom
  // sem a permissão via o botão e ganhava FORBIDDEN só depois de escolher o
  // período no modal.
  const { can } = usePermissions();
  const canExport = can("email:export_reports");
  const [activeTab, setActiveTab] = useState("enviados");
  const [searchQuery, setSearchQuery] = useState("");
  const [periodFilter, setPeriodFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortBy, setSortBy] = useState("date");
  const [exportOpen, setExportOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [newEmailName, setNewEmailName] = useState("");
  const [newEmailType, setNewEmailType] = useState<"single" | "ab_test" | "campaign" | "automation">("single");
  // Dialog de detalhes/relatório de uma mensagem enviada
  const [messageDetailId, setMessageDetailId] = useState<number | null>(null);

  // ─── Queries ────────────────────────────────────────────────────────────────
  const { data: statsData } = trpc.sendgrid.history.globalStats.useQuery();
  const { data: sentCampaignsData, isLoading: loadingSentCampaigns } = trpc.emailCampaigns.listSent.useQuery({ limit: 100 });
  const sentCampaigns = sentCampaignsData?.rows ?? [];
  const { data: schedulesData, isLoading: loadingSchedules } = trpc.emailCampaigns.list.useQuery({ status: "scheduled", limit: 200 });
  const { data: templates = [], isLoading: loadingTemplates } = trpc.emailTemplates.list.useQuery();

  useContextoDeLista({
    titulo: "E-mail: painel",
    itens: ((sentCampaignsData as any)?.rows ?? []).slice(0, 10).map((c: any) => ({
      id: c.id, nome: c.name, assunto: c.subject, enviadaEm: c.sentAt,
    })),
    extra: {
      agendadas: ((schedulesData as any)?.rows ?? []).length,
      estatisticasGerais: statsData ?? null,
    },
  });

  const { data: draftsData, isLoading: loadingDrafts } = trpc.emailCampaigns.list.useQuery({ status: "draft" });
  const drafts = (draftsData && 'rows' in draftsData ? draftsData.rows : draftsData) ?? [];

  const cancelMutation = trpc.emailCampaigns.update.useMutation({
    onSuccess: () => {
      toast.success("Agendamento cancelado.");
      utils.emailCampaigns.list.invalidate();
    },
    onError: (err) => toast.error(toUserFriendlyError(err, "Erro ao cancelar agendamento")),
  });

  const deleteTemplateMutation = trpc.emailTemplates.delete.useMutation({
    onSuccess: () => {
      toast.success("Template excluído.");
      utils.emailTemplates.list.invalidate();
    },
    onError: (err) => toast.error(toUserFriendlyError(err, "Erro ao excluir template")),
  });

  const createCampaignMutation = trpc.emailCampaigns.create.useMutation({
    onSuccess: (data) => {
      setCreateOpen(false);
      setNewEmailName("");
      if (data?.id) {
        // Vai direto pro editor (sem stepper).
        // Para A/B Test, sinaliza ao editor abrir o painel A/B (Fase 5).
        const suffix = newEmailType === "ab_test" ? "?ab=1" : "";
        navigate(`/emails/${data.id}/editor${suffix}`);
      }
    },
    onError: (err) => toast.error(toUserFriendlyError(err, "Erro ao criar")),
  });

  // Criação de Campanha (kind=campaign) ou Automação (kind=automation) — usa flowsRouter
  const createFlowMutation = trpc.flows.create.useMutation({
    onSuccess: (data: any) => {
      setCreateOpen(false);
      setNewEmailName("");
      if (data?.id) navigate(`/flows/${data.id}`);
    },
    onError: (err) => toast.error(toUserFriendlyError(err, "Erro ao criar")),
  });

  const deleteCampaignMutation = trpc.emailCampaigns.delete.useMutation({
    onSuccess: () => {
      toast.success("Rascunho excluído.");
      utils.emailCampaigns.list.invalidate();
    },
    onError: (err) => toast.error(toUserFriendlyError(err, "Erro ao excluir rascunho")),
  });

  const utils = trpc.useUtils();

  const handleCreateEmail = () => {
    if (!newEmailName.trim()) {
      toast.warning("Informe um nome.");
      return;
    }
    if (newEmailType === "campaign" || newEmailType === "automation") {
      createFlowMutation.mutate({
        name: newEmailName.trim(),
        channel: "email",
        kind: newEmailType,
        enrollmentMode: newEmailType === "campaign" ? "bulk_enroll" : "trigger_event",
        triggerType: newEmailType === "campaign" ? "manual" : "keyword",
      });
      return;
    }
    createCampaignMutation.mutate({ name: newEmailName.trim() });
  };

  const stats = statsData ?? { total: 0, sent: 0, delivered: 0, opened: 0, clicked: 0, bounced: 0, failed: 0 };
  const scheduledRows = (schedulesData && 'rows' in schedulesData ? schedulesData.rows : schedulesData) ?? [];

  // ─── Filtering ──────────────────────────────────────────────────────────────

  const filteredSchedules = useMemo(() => {
    let rows = [...scheduledRows];

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      rows = rows.filter((r: any) => r.subject?.toLowerCase().includes(q));
    }

    return rows;
  }, [scheduledRows, searchQuery]);

  const filteredTemplates = useMemo(() => {
    if (!searchQuery) return templates;
    const q = searchQuery.toLowerCase();
    return templates.filter(
      (t: any) =>
        t.name?.toLowerCase().includes(q) ||
        t.subject?.toLowerCase().includes(q)
    );
  }, [templates, searchQuery]);

  // ─── Filtering: Campanhas enviadas (aba "Enviados") ────────────────────────
  const filteredSentCampaigns = useMemo(() => {
    let rows = [...sentCampaigns];
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      rows = rows.filter(
        (r: any) => r.name?.toLowerCase().includes(q) || r.subject?.toLowerCase().includes(q),
      );
    }
    if (periodFilter !== "all") {
      const since = getDateFromPeriod(periodFilter);
      if (since) {
        rows = rows.filter((r: any) => {
          const d = r.sentAt ? new Date(r.sentAt) : new Date(r.createdAt);
          return d >= since;
        });
      }
    }
    if (sortBy === "alpha") {
      rows.sort((a: any, b: any) => (a.name ?? "").localeCompare(b.name ?? ""));
    }
    return rows;
  }, [sentCampaigns, searchQuery, periodFilter, sortBy]);

  // ─── Tab counts ─────────────────────────────────────────────────────────────
  const sentCount = sentCampaigns.length;
  const draftCount = drafts.length;
  const scheduledCount = scheduledRows.filter((r: any) => r.status === "scheduled").length;
  const templateCount = templates.length;

  // ─── Render ─────────────────────────────────────────────────────────────────
  return (
    <PermissionGuard permission="email:view_campaigns">
    <DashboardLayout>
      <div className="space-y-6 p-4 sm:p-6 max-w-full overflow-x-hidden">
        {/* ── Header ─────────────────────────────────────────────────────────── */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            <h1 className="text-2xl sm:text-3xl font-bold">Email</h1>
            <p className="text-sm sm:text-base text-muted-foreground mt-1">
              Gerencie seus e-mails enviados, agendados e modelos
            </p>
          </div>
          <div className="flex items-center gap-2 sm:shrink-0">
            {/* Higiene mora aqui e não em Contatos: quem cuida de entrega é
                quem manda campanha, e é olhando o bounce da última edição que
                a pessoa lembra de limpar a base. */}
            <Button
              variant="outline"
              onClick={() => navigate("/emails/higiene")}
              className="gap-2"
              title="Endereços que não recebem mais e-mail"
            >
              <HeartPulse className="h-4 w-4" />
              <span className="hidden sm:inline">Higiene</span>
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={() => navigate("/settings")}
              title="Configurações de e-mail"
            >
              <Settings className="h-4 w-4" />
            </Button>
            <Button onClick={() => setCreateOpen(true)} className="gap-2 flex-1 sm:flex-initial">
              <Plus className="h-4 w-4" />
              Criar Email
            </Button>
          </div>
        </div>

        {/* ── Stats ──────────────────────────────────────────────────────────── */}
        <StatsBar stats={stats} />

        {/* ── Filters ────────────────────────────────────────────────────────── */}
        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          <div className="relative w-full sm:flex-1 sm:min-w-[200px] sm:max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>

          <Select value={periodFilter} onValueChange={setPeriodFilter}>
            <SelectTrigger className="flex-1 sm:flex-initial sm:w-[160px]">
              <SelectValue placeholder="Período" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todo período</SelectItem>
              <SelectItem value="today">Hoje</SelectItem>
              <SelectItem value="7d">Últimos 7 dias</SelectItem>
              <SelectItem value="14d">Últimos 14 dias</SelectItem>
              <SelectItem value="30d">Últimos 30 dias</SelectItem>
              <SelectItem value="6m">Últimos 6 meses</SelectItem>
            </SelectContent>
          </Select>

          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="flex-1 sm:flex-initial sm:w-[140px]">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="sent">Enviado</SelectItem>
              <SelectItem value="delivered">Entregue</SelectItem>
              <SelectItem value="opened">Aberto</SelectItem>
              <SelectItem value="clicked">Clicado</SelectItem>
              <SelectItem value="bounced">Rejeitado</SelectItem>
              <SelectItem value="failed">Falhou</SelectItem>
            </SelectContent>
          </Select>

          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="flex-1 sm:flex-initial sm:w-[160px]">
              <SelectValue placeholder="Ordenar" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="date">Data de envio</SelectItem>
              <SelectItem value="alpha">Ordem alfabética</SelectItem>
            </SelectContent>
          </Select>

          {canExport && (
            <Button variant="outline" className="gap-2" onClick={() => setExportOpen(true)}>
              <Download className="h-4 w-4" />
              Exportar
            </Button>
          )}
        </div>

        {/* ── Tabs ───────────────────────────────────────────────────────────── */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="enviados" className="gap-2">
              <Send className="h-4 w-4" />
              Enviados
              <Badge variant="secondary" className="ml-1 text-xs">{sentCount}</Badge>
            </TabsTrigger>
            <TabsTrigger value="rascunhos" className="gap-2">
              <FileEdit className="h-4 w-4" />
              Rascunhos
              <Badge variant="secondary" className="ml-1 text-xs">{draftCount}</Badge>
            </TabsTrigger>
            <TabsTrigger value="agendados" className="gap-2">
              <Clock className="h-4 w-4" />
              Agendados
              <Badge variant="secondary" className="ml-1 text-xs">{scheduledCount}</Badge>
            </TabsTrigger>
            <TabsTrigger value="modelos" className="gap-2">
              <LayoutTemplate className="h-4 w-4" />
              Modelos
              <Badge variant="secondary" className="ml-1 text-xs">{templateCount}</Badge>
            </TabsTrigger>
          </TabsList>

          {/* ── Tab: Enviados (campanhas) ────────────────────────────────── */}
          <TabsContent value="enviados" className="mt-4">
            {loadingSentCampaigns ? (
              <p className="text-sm text-muted-foreground text-center py-12">Carregando...</p>
            ) : filteredSentCampaigns.length === 0 ? (
              <TabEmptyState
                icon={Send}
                title="As campanhas que você enviar ficarão salvas aqui"
                description="Que tal enviar uma agora?"
                actionLabel="Criar Email"
                onAction={() => setCreateOpen(true)}
              />
            ) : (
              <Card className="overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/40 border-b">
                      <tr>
                        <th className="text-left p-3 font-medium text-muted-foreground">Campanha</th>
                        <th className="hidden md:table-cell text-left p-3 font-medium text-muted-foreground">Destinatários</th>
                        <th className="text-left p-3 font-medium text-muted-foreground">Aberturas</th>
                        <th className="hidden md:table-cell text-left p-3 font-medium text-muted-foreground">Cliques</th>
                        <th className="hidden sm:table-cell text-left p-3 font-medium text-muted-foreground">Status</th>
                        <th className="hidden lg:table-cell text-left p-3 font-medium text-muted-foreground">Data de envio</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredSentCampaigns.map((row: any) => {
                        const total = row.totalRecipients || row.messages || 0;
                        const opened = row.opened || 0;
                        const clicked = row.clicked || 0;
                        const delivered = row.delivered || 0;
                        const denomEng = delivered > 0 ? delivered : (row.messages || 0);
                        const openRate = denomEng > 0 ? Math.min(100, Math.round((opened / denomEng) * 100)) : 0;
                        const clickRate = opened > 0 ? Math.min(100, Math.round((clicked / opened) * 100)) : 0;
                        const statusLabel = row.status === "sending" ? "Enviando"
                          : row.status === "cancelled" ? "Interrompida"
                          : "Enviado";
                        const statusVariant: "default" | "secondary" | "outline" =
                          row.status === "sending" ? "secondary"
                          : row.status === "cancelled" ? "outline"
                          : "default";
                        return (
                          <tr
                            key={row.id}
                            className="border-b last:border-0 hover:bg-muted/20 transition-colors cursor-pointer"
                            onClick={() => navigate(`/emails/campaigns/${row.id}`)}
                          >
                            <td className="p-3 max-w-[180px] sm:max-w-[280px]">
                              <div className="font-medium truncate">{row.name}</div>
                              <div className="text-xs text-muted-foreground truncate">{row.subject || "Sem assunto"}</div>
                              {/* Em mobile mostra data + status compactos abaixo do título */}
                              <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[10px] text-muted-foreground sm:hidden">
                                <Badge variant={statusVariant} className="text-[10px] px-1.5 py-0">{statusLabel}</Badge>
                                <span>·</span>
                                <span>
                                  {row.sentAt
                                    ? new Date(row.sentAt).toLocaleDateString("pt-BR")
                                    : new Date(row.createdAt).toLocaleDateString("pt-BR")}
                                </span>
                              </div>
                            </td>
                            <td className="hidden md:table-cell p-3 text-xs whitespace-nowrap">
                              {row.messages || 0}{total > 0 && total !== row.messages ? ` / ${total}` : ""}
                            </td>
                            <td className="p-3 text-xs whitespace-nowrap">
                              {opened} <span className="text-muted-foreground">({openRate}%)</span>
                            </td>
                            <td className="hidden md:table-cell p-3 text-xs whitespace-nowrap">
                              {clicked} <span className="text-muted-foreground">({clickRate}%)</span>
                            </td>
                            <td className="hidden sm:table-cell p-3">
                              <Badge variant={statusVariant}>{statusLabel}</Badge>
                            </td>
                            <td className="hidden lg:table-cell p-3 text-muted-foreground text-xs whitespace-nowrap">
                              {row.sentAt
                                ? new Date(row.sentAt).toLocaleString("pt-BR")
                                : new Date(row.createdAt).toLocaleString("pt-BR")}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}
          </TabsContent>

          {/* ── Tab: Rascunhos ─────────────────────────────────────────────── */}
          <TabsContent value="rascunhos" className="mt-4">
            {loadingDrafts ? (
              <p className="text-sm text-muted-foreground text-center py-12">Carregando...</p>
            ) : drafts.length === 0 ? (
              <TabEmptyState
                icon={FileEdit}
                title="Seus rascunhos aparecerão aqui"
                description="Ao criar um e-mail pelo fluxo de etapas, ele será salvo automaticamente como rascunho até ser enviado."
                actionLabel="Criar Email"
                onAction={() => setCreateOpen(true)}
              />
            ) : (
              <Card className="overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/40 border-b">
                      <tr>
                        <th className="text-left p-3 font-medium text-muted-foreground">Nome</th>
                        <th className="hidden md:table-cell text-left p-3 font-medium text-muted-foreground">Assunto</th>
                        <th className="hidden lg:table-cell text-left p-3 font-medium text-muted-foreground">Atualizado em</th>
                        <th className="hidden sm:table-cell text-left p-3 font-medium text-muted-foreground">Status</th>
                        <th className="text-right p-3 font-medium text-muted-foreground w-10"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {drafts.map((draft: any) => (
                        <tr
                          key={draft.id}
                          className="border-b last:border-0 hover:bg-muted/20 transition-colors cursor-pointer"
                          onClick={() => navigate(`/emails/${draft.id}/editor`)}
                        >
                          <td className="p-3 max-w-[200px]">
                            <div className="font-medium truncate">{draft.name}</div>
                            <div className="mt-0.5 text-xs text-muted-foreground truncate md:hidden">
                              {draft.subject || "Sem assunto"}
                            </div>
                            <div className="mt-1 flex items-center gap-1.5 text-[10px] text-muted-foreground sm:hidden">
                              <Badge variant="secondary" className="text-[10px] px-1.5 py-0">Rascunho</Badge>
                              <span>· {new Date(draft.updatedAt).toLocaleDateString("pt-BR")}</span>
                            </div>
                          </td>
                          <td className="hidden md:table-cell p-3 truncate max-w-[280px] text-muted-foreground">
                            {draft.subject || "Sem assunto"}
                          </td>
                          <td className="hidden lg:table-cell p-3 text-muted-foreground text-xs whitespace-nowrap">
                            {new Date(draft.updatedAt).toLocaleString("pt-BR")}
                          </td>
                          <td className="hidden sm:table-cell p-3">
                            <Badge variant="secondary">Rascunho</Badge>
                          </td>
                          <td className="p-3 text-right" onClick={(e) => e.stopPropagation()}>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" className="h-8 w-8" aria-label="Mais opções">
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem
                                  className="gap-2"
                                  onClick={() => navigate(`/emails/${draft.id}/editor`)}
                                >
                                  <Pencil className="h-4 w-4" />
                                  Editar
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  className="gap-2 text-destructive"
                                  onClick={() => deleteCampaignMutation.mutate({ id: draft.id })}
                                >
                                  <Trash2 className="h-4 w-4" />
                                  Excluir
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}
          </TabsContent>

          {/* ── Tab: Agendados ─────────────────────────────────────────────── */}
          <TabsContent value="agendados" className="mt-4">
            {loadingSchedules ? (
              <p className="text-sm text-muted-foreground text-center py-12">Carregando...</p>
            ) : filteredSchedules.length === 0 ? (
              <TabEmptyState
                icon={Clock}
                title="Nenhum email agendado"
                description="Agende emails para envio futuro e acompanhe aqui."
                actionLabel="Criar Email"
                onAction={() => setCreateOpen(true)}
              />
            ) : (
              <Card className="overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/40 border-b">
                      <tr>
                        <th className="text-left p-3 font-medium text-muted-foreground">Assunto</th>
                        <th className="hidden md:table-cell text-left p-3 font-medium text-muted-foreground">Destinatário</th>
                        <th className="hidden sm:table-cell text-left p-3 font-medium text-muted-foreground">Agendado para</th>
                        <th className="hidden sm:table-cell text-left p-3 font-medium text-muted-foreground">Status</th>
                        <th className="text-right p-3 font-medium text-muted-foreground w-10"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredSchedules.map((row: any) => {
                        const s = SCHEDULE_STATUS_CONFIG[row.status] ?? { label: row.status, variant: "outline" as const };
                        const segmentCount = Array.isArray(row.segmentIds) ? row.segmentIds.length : 0;
                        const tagCount = Array.isArray(row.tagIds) ? row.tagIds.length : 0;
                        const recipientLabel = segmentCount + tagCount === 0
                          ? "Sem destinatários"
                          : [
                              segmentCount > 0 ? `${segmentCount} ${segmentCount === 1 ? "segmento" : "segmentos"}` : null,
                              tagCount > 0 ? `${tagCount} ${tagCount === 1 ? "tag" : "tags"}` : null,
                            ].filter(Boolean).join(" + ");
                        return (
                          <tr key={row.id} className="border-b last:border-0 hover:bg-muted/20 transition-colors">
                            <td className="p-3 max-w-[200px] sm:max-w-[280px]">
                              <div className="truncate">{row.subject ?? row.name}</div>
                              <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[10px] text-muted-foreground sm:hidden">
                                <Badge variant={s.variant} className="text-[10px] px-1.5 py-0">{s.label}</Badge>
                                <span>·</span>
                                <span>{row.scheduledAt ? new Date(row.scheduledAt).toLocaleString("pt-BR") : "—"}</span>
                              </div>
                            </td>
                            <td className="hidden md:table-cell p-3 text-xs text-muted-foreground">
                              <Badge variant="outline" className="font-normal">
                                {recipientLabel}
                              </Badge>
                            </td>
                            <td className="hidden sm:table-cell p-3 text-xs whitespace-nowrap">
                              {row.scheduledAt
                                ? new Date(row.scheduledAt).toLocaleString("pt-BR")
                                : "—"}
                            </td>
                            <td className="hidden sm:table-cell p-3">
                              <Badge variant={s.variant}>{s.label}</Badge>
                            </td>
                            <td className="p-3 text-right">
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="icon" className="h-8 w-8" aria-label="Ações do agendamento">
                                    <MoreHorizontal className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem
                                    className="gap-2"
                                    onClick={() => navigate(`/emails/${row.id}/editor`)}
                                  >
                                    <Eye className="h-4 w-4" />
                                    Ver detalhes
                                  </DropdownMenuItem>
                                  {row.status === "scheduled" && (
                                    <DropdownMenuItem
                                      className="gap-2 text-destructive"
                                      onClick={() => cancelMutation.mutate({ id: row.id, status: "draft", scheduledAt: null })}
                                    >
                                      <XCircle className="h-4 w-4" />
                                      Cancelar agendamento
                                    </DropdownMenuItem>
                                  )}
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}
          </TabsContent>

          {/* ── Tab: Modelos ───────────────────────────────────────────────── */}
          <TabsContent value="modelos" className="mt-4">
            <div className="space-y-4">
              {/* Link to full templates page */}
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm text-muted-foreground">
                  Seus templates salvos e modelos prontos do sistema.
                </p>
                <Button
                  variant="outline"
                  className="gap-2 sm:shrink-0"
                  onClick={() => navigate("/emails/templates")}
                >
                  <LayoutTemplate className="h-4 w-4" />
                  Ver todos os modelos
                </Button>
              </div>

              {loadingTemplates ? (
                <p className="text-sm text-muted-foreground text-center py-12">Carregando...</p>
              ) : filteredTemplates.length === 0 ? (
                <TabEmptyState
                  icon={LayoutTemplate}
                  title="Nenhum modelo salvo"
                  description="Crie um e-mail no editor e salve como template, ou explore os modelos prontos."
                  actionLabel="Explorar modelos prontos"
                  onAction={() => navigate("/emails/templates")}
                />
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                  {filteredTemplates.map((tpl: any) => (
                    <Card key={tpl.id} className="flex flex-col">
                      <div className="p-4 flex-1">
                        <div className="flex items-start justify-between">
                          <div className="min-w-0 flex-1">
                            <h3 className="font-medium truncate">{tpl.name}</h3>
                            <p className="text-sm text-muted-foreground truncate mt-0.5">{tpl.subject}</p>
                          </div>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" aria-label="Mais opções">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                className="gap-2"
                                onClick={() => navigate("/emails/create")}
                              >
                                <FolderOpen className="h-4 w-4" />
                                Usar no Editor
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                className="gap-2 text-destructive"
                                onClick={() => deleteTemplateMutation.mutate({ id: tpl.id })}
                              >
                                <Trash2 className="h-4 w-4" />
                                Excluir
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                        <div className="flex items-center gap-2 mt-3 text-xs text-muted-foreground">
                          <span>{tpl.blocks ? `${(tpl.blocks as any[]).length} bloco(s)` : "Sem blocos"}</span>
                          <span>·</span>
                          <span>{new Date(tpl.updatedAt).toLocaleDateString("pt-BR")}</span>
                        </div>
                      </div>
                      <Separator />
                      <div className="p-3">
                        <Button
                          size="sm"
                          variant="outline"
                          className="w-full gap-2"
                          onClick={() => navigate("/emails/create")}
                        >
                          <FolderOpen className="h-3.5 w-3.5" />
                          Usar no Editor
                        </Button>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* ── Export Modal ─────────────────────────────────────────────────────── */}
      <ExportModal open={exportOpen} onClose={() => setExportOpen(false)} />

      {/* ── Create Email Modal ────────────────────────────────────────────────── */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="w-[95vw] max-w-md mx-2 sm:mx-0">
          <DialogHeader>
            <DialogTitle>Criar novo e-mail</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-sm font-medium">Nome do e-mail</Label>
              <p className="text-xs text-muted-foreground mb-1.5">
                Escolha um nome para identificar seu e-mail
              </p>
              <Input
                value={newEmailName}
                onChange={(e) => setNewEmailName(e.target.value)}
                placeholder="Ex: Newsletter Março 2026"
                onKeyDown={(e) => { if (e.key === "Enter") handleCreateEmail(); }}
                autoFocus
              />
            </div>
            <div>
              <Label className="text-sm font-medium">Tipo</Label>
              <div className="mt-2 space-y-2">
                <label className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${newEmailType === "single" ? "border-primary bg-primary/5" : "border-border"}`}>
                  <input
                    type="radio"
                    name="emailType"
                    value="single"
                    checked={newEmailType === "single"}
                    onChange={() => setNewEmailType("single")}
                  />
                  <div>
                    <span className="text-sm font-medium">E-mail único</span>
                    <p className="text-xs text-muted-foreground">Envie para sua base de contatos (newsletter, broadcast)</p>
                  </div>
                </label>
                <label className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${newEmailType === "ab_test" ? "border-primary bg-primary/5" : "border-border"}`}>
                  <input
                    type="radio"
                    name="emailType"
                    value="ab_test"
                    checked={newEmailType === "ab_test"}
                    onChange={() => setNewEmailType("ab_test")}
                  />
                  <div>
                    <span className="text-sm font-medium">Teste A/B de Assunto</span>
                    <p className="text-xs text-muted-foreground">2 variantes de assunto, vencedor escolhido por taxa de abertura</p>
                  </div>
                </label>
                <label className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${newEmailType === "campaign" ? "border-primary bg-primary/5" : "border-border"}`}>
                  <input
                    type="radio"
                    name="emailType"
                    value="campaign"
                    checked={newEmailType === "campaign"}
                    onChange={() => setNewEmailType("campaign")}
                  />
                  <div>
                    <span className="text-sm font-medium">Campanha</span>
                    <p className="text-xs text-muted-foreground">Sequência programada (lançamento, série de boas-vindas) — enrollment em massa</p>
                  </div>
                </label>
                <label className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${newEmailType === "automation" ? "border-primary bg-primary/5" : "border-border"}`}>
                  <input
                    type="radio"
                    name="emailType"
                    value="automation"
                    checked={newEmailType === "automation"}
                    onChange={() => setNewEmailType("automation")}
                  />
                  <div>
                    <span className="text-sm font-medium">Automação</span>
                    <p className="text-xs text-muted-foreground">Disparo por evento (carrinho abandonado, tag, comportamento)</p>
                  </div>
                </label>
              </div>
            </div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setCreateOpen(false);
                navigate("/emails/templates");
              }}
              className="gap-2"
            >
              <LayoutTemplate className="h-4 w-4" />
              Usar modelo pronto
            </Button>
            <div className="flex gap-2 ml-auto">
              <Button variant="outline" onClick={() => setCreateOpen(false)}>
                Cancelar
              </Button>
              <Button
                onClick={handleCreateEmail}
                disabled={createCampaignMutation.isPending || createFlowMutation.isPending}
                className="gap-2"
              >
                <Plus className="h-4 w-4" />
                {createCampaignMutation.isPending || createFlowMutation.isPending
                  ? "Criando..."
                  : newEmailType === "campaign" || newEmailType === "automation"
                    ? `Criar ${newEmailType === "campaign" ? "campanha" : "automação"}`
                    : "Criar email"}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Dialog: Detalhes / Relatório de uma mensagem ─────────────────── */}
      <MessageDetailDialog
        messageId={messageDetailId}
        initialTab="details"
        onClose={() => setMessageDetailId(null)}
      />
    </DashboardLayout>
    </PermissionGuard>
  );
}

// ─── MessageDetailDialog ────────────────────────────────────────────────────

function MessageDetailDialog({
  messageId,
  initialTab,
  onClose,
}: {
  messageId: number | null;
  initialTab: "details" | "report";
  onClose: () => void;
}) {
  const open = messageId !== null;
  const { data, isLoading } = trpc.sendgrid.history.getMessage.useQuery(
    { id: messageId ?? 0 },
    { enabled: open },
  );

  const message = data?.message;
  const events = data?.events ?? [];

  const eventLabel: Record<string, string> = {
    delivered: "Entregue",
    open: "Aberto",
    click: "Clicado",
    bounce: "Bounced",
    dropped: "Descartado",
    unsubscribe: "Descadastrado",
    spamreport: "Marcado como spam",
    deferred: "Adiado",
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="w-[95vw] max-w-3xl max-h-[85vh] overflow-y-auto mx-2 sm:mx-0">
        <DialogHeader>
          <DialogTitle className="truncate">
            {message?.subject ?? "Detalhes da mensagem"}
          </DialogTitle>
        </DialogHeader>

        {isLoading && (
          <p className="text-sm text-muted-foreground py-8 text-center">Carregando…</p>
        )}

        {!isLoading && !message && (
          <p className="text-sm text-muted-foreground py-8 text-center">
            Mensagem não encontrada.
          </p>
        )}

        {message && (
          <Tabs defaultValue={initialTab} className="mt-2">
            <TabsList>
              <TabsTrigger value="details" className="gap-2">
                <Eye className="h-4 w-4" /> Detalhes
              </TabsTrigger>
              <TabsTrigger value="report" className="gap-2">
                <BarChart2 className="h-4 w-4" /> Relatório
              </TabsTrigger>
            </TabsList>

            <TabsContent value="details" className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <Label className="text-xs text-muted-foreground">Destinatário</Label>
                  <p className="font-mono text-xs mt-0.5 break-all">{message.to}</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Status</Label>
                  <div className="mt-0.5">
                    <Badge variant={EMAIL_STATUS_CONFIG[message.status]?.variant ?? "outline"}>
                      {EMAIL_STATUS_CONFIG[message.status]?.label ?? message.status}
                    </Badge>
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Enviado em</Label>
                  <p className="text-xs mt-0.5">
                    {message.sentAt
                      ? new Date(message.sentAt).toLocaleString("pt-BR")
                      : "—"}
                  </p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">SendGrid Message ID</Label>
                  <p className="font-mono text-xs mt-0.5 break-all">
                    {message.sendgridMessageId || "—"}
                  </p>
                </div>
              </div>

              <Separator />

              <div>
                <Label className="text-xs text-muted-foreground">Pré-visualização</Label>
                {message.html ? (
                  <iframe
                    title="email-preview"
                    srcDoc={message.html}
                    sandbox=""
                    className="mt-2 w-full h-[400px] border rounded-lg bg-white"
                  />
                ) : (
                  <p className="text-xs text-muted-foreground mt-2">
                    HTML não disponível.
                  </p>
                )}
              </div>
            </TabsContent>

            <TabsContent value="report" className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <Label className="text-xs text-muted-foreground">Status atual</Label>
                  <div className="mt-0.5">
                    <Badge variant={EMAIL_STATUS_CONFIG[message.status]?.variant ?? "outline"}>
                      {EMAIL_STATUS_CONFIG[message.status]?.label ?? message.status}
                    </Badge>
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Total de eventos</Label>
                  <p className="text-sm font-medium mt-0.5">{events.length}</p>
                </div>
              </div>

              <Separator />

              <div>
                <Label className="text-xs text-muted-foreground">Timeline de eventos (SendGrid webhook)</Label>
                {events.length === 0 ? (
                  <p className="text-xs text-muted-foreground mt-2">
                    Nenhum evento recebido ainda. Eventos aparecem conforme o SendGrid
                    notifica entrega, abertura, clique, etc. (pode levar minutos).
                  </p>
                ) : (
                  <ol className="mt-3 space-y-2">
                    {events.map((ev: any) => (
                      <li
                        key={ev.id}
                        className="flex items-start gap-3 text-sm border-l-2 border-primary/30 pl-3 py-1"
                      >
                        <Badge variant="secondary" className="text-xs shrink-0 mt-0.5">
                          {eventLabel[ev.eventType] ?? ev.eventType}
                        </Badge>
                        <span className="text-xs text-muted-foreground">
                          {ev.timestamp ? new Date(ev.timestamp).toLocaleString("pt-BR") : "—"}
                        </span>
                      </li>
                    ))}
                  </ol>
                )}
              </div>
            </TabsContent>
          </Tabs>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Fechar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
